import React, { useEffect } from 'react';
import { motion, useAnimation } from 'framer-motion';
import { CLASSES } from '../game/classes';

// v3.7 COMBAT AVATAR: a stylized "fighter" that reacts to the last card played.
// v3.8 PER-CLASS POSES: idle bob varies by class.
// v3.9 PER-CLASS ATTACK ANIMS: each class attacks differently:
//   RIZZLER → blow a kiss (💋 projectile drifts toward enemy) + coy tilt
//   SIGMA   → double flex, hard-lunge horizontal charge
//   GOONER  → shoulder-bash (heavy step-in + squash)
const CLASS_POSE = {
  rizzler: { idleClass: 'pose-idle-rizzler', accent: '💫', weapon: '💋', attackKind: 'kiss' },
  sigma:   { idleClass: 'pose-idle-sigma',   accent: '💪', weapon: '⚔',  attackKind: 'flex' },
  gooner:  { idleClass: 'pose-idle-gooner',  accent: '⚓', weapon: '🛡', attackKind: 'bash' },
};

// Actions: 'idle' | 'attack' | 'block' | 'power' | 'hurt'
export default function PlayerFighter({ classId, action, trigger, hurt, size = 96 }) {
  const controls = useAnimation();
  const weaponControls = useAnimation();
  const auraControls = useAnimation();
  const shieldControls = useAnimation();
  const projectileControls = useAnimation();
  const classDef = CLASSES[classId] || CLASSES.rizzler;
  const color = classDef.color;
  const icon = classDef.icon;
  const pose = CLASS_POSE[classId] || CLASS_POSE.rizzler;

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (cancelled) return;
      if (action === 'attack') {
        if (pose.attackKind === 'kiss') {
          // RIZZLER: coy tilt + wink, kiss projectile drifts right toward enemy
          await controls.start({ x: 24, rotate: -12, scale: 1.06, transition: { duration: 0.14, ease: 'easeOut' } });
          // v3.9 fix: single-shot projectile animation with from/to via keyframes (no .set()+.start() race)
          projectileControls.start({
            opacity: [0, 1, 1, 0],
            x:      [40, 100, 220, 300],
            y:      [-20, -35, -55, -70],
            scale:  [0.8, 1.2, 1.6, 1.3],
            rotate: [-10, 0, 12, 24],
            transition: { duration: 0.7, times: [0, 0.15, 0.85, 1], ease: 'easeOut' },
          }).catch(() => {});
          if (cancelled) return;
          await controls.start({ x: 0, rotate: 0, scale: 1, transition: { type: 'spring', stiffness: 320, damping: 16 } });
        } else if (pose.attackKind === 'flex') {
          // SIGMA: double-flex windup then long lunge
          await controls.start({ x: -18, scale: 1.14, rotate: -4, transition: { duration: 0.14 } });
          if (cancelled) return;
          await controls.start({ x: 140, rotate: -14, scale: 1.12, transition: { duration: 0.16, ease: 'easeIn' } });
          weaponControls.start({ opacity: 1, x: 46, rotate: 28, transition: { duration: 0.14 } });
          if (cancelled) return;
          await controls.start({ x: 0, rotate: 0, scale: 1, transition: { type: 'spring', stiffness: 360, damping: 18 } });
          weaponControls.start({ opacity: 0, x: 0, rotate: 0, transition: { duration: 0.2 } });
        } else if (pose.attackKind === 'bash') {
          // GOONER: heavy squash then shoulder-charge
          await controls.start({ scaleX: 1.2, scaleY: 0.85, x: -8, transition: { duration: 0.12 } });
          if (cancelled) return;
          await controls.start({ x: 130, scaleX: 1.14, scaleY: 1.06, rotate: -8, transition: { duration: 0.14, ease: 'easeIn' } });
          weaponControls.start({ opacity: 0.9, x: 20, rotate: -15, transition: { duration: 0.12 } });
          if (cancelled) return;
          await controls.start({ x: 0, scaleX: 1, scaleY: 1, rotate: 0, transition: { type: 'spring', stiffness: 260, damping: 15 } });
          weaponControls.start({ opacity: 0, x: 0, rotate: 0, transition: { duration: 0.2 } });
        }
      } else if (action === 'block') {
        shieldControls.start({ opacity: 1, scale: 1, transition: { type: 'spring', stiffness: 500, damping: 12 } });
        await controls.start({ y: -8, scale: 1.04, transition: { duration: 0.12 } });
        if (cancelled) return;
        await controls.start({ y: 0, scale: 1, transition: { type: 'spring', stiffness: 260, damping: 16 } });
        shieldControls.start({ opacity: 0, scale: 0.6, transition: { duration: 0.4, delay: 0.15 } });
      } else if (action === 'power') {
        auraControls.start({ opacity: 0.9, scale: 2.4, transition: { duration: 0.5 } });
        await controls.start({ y: -14, scale: 1.12, rotate: 4, transition: { duration: 0.25 } });
        if (cancelled) return;
        await controls.start({ y: 0, scale: 1, rotate: 0, transition: { type: 'spring', stiffness: 220, damping: 15 } });
        auraControls.start({ opacity: 0, scale: 0.5, transition: { duration: 0.4 } });
      } else if (action === 'hurt') {
        await controls.start({ x: -18, rotate: 8, transition: { duration: 0.1 } });
        if (cancelled) return;
        await controls.start({ x: 0, rotate: 0, transition: { type: 'spring', stiffness: 300, damping: 12 } });
      }
    };
    run();
    return () => { cancelled = true; };
  }, [action, trigger]);

  return (
    <div
      className="player-fighter-wrap"
      style={{ width: size + 40, height: size + 60 }}
      data-testid="player-fighter"
      data-action={action}
      data-attack-kind={pose.attackKind}
    >
      <div className="player-fighter-shadow" style={{ borderColor: color }} />

      <motion.div
        className="player-fighter-aura"
        initial={{ opacity: 0, scale: 0.5 }}
        animate={auraControls}
        style={{ background: `radial-gradient(circle, ${color}66 0%, transparent 65%)` }}
      />

      <motion.div
        className={`player-fighter-body ${hurt ? 'is-hurt' : ''}`}
        initial={{ y: 0, x: 0, rotate: 0, scale: 1 }}
        animate={controls}
        style={{
          fontSize: size,
          textShadow: `0 0 26px ${color}, 4px 4px 0 rgba(0,0,0,0.9)`,
          filter: `drop-shadow(0 6px 0 rgba(0,0,0,0.7))`,
        }}
      >
        <div className={`player-fighter-idlebob ${pose.idleClass}`}>
          <span aria-hidden="true">{icon}</span>
          <span className="player-fighter-accent" aria-hidden="true">{pose.accent}</span>
        </div>

        <motion.div
          className="player-fighter-weapon"
          initial={{ opacity: 0, x: 0, rotate: 0 }}
          animate={weaponControls}
          style={{ color, textShadow: `0 0 18px ${color}` }}
        >{pose.weapon}</motion.div>

        <motion.div
          className="player-fighter-shield"
          initial={{ opacity: 0, scale: 0.6 }}
          animate={shieldControls}
          style={{ color, textShadow: `0 0 18px ${color}` }}
        >🛡</motion.div>

        {/* v3.9 class-specific projectile (kiss for rizzler, no-op others) */}
        <motion.div
          className="player-fighter-projectile"
          initial={{ opacity: 0, x: 40, y: -20, scale: 0.8 }}
          animate={projectileControls}
          style={{ color, textShadow: `0 0 22px ${color}` }}
          data-testid={`fighter-projectile-${pose.attackKind}`}
          aria-hidden="true"
        >{pose.attackKind === 'kiss' ? '💋' : ''}</motion.div>
      </motion.div>

      <div className="player-fighter-label" style={{ color, borderColor: color }}>
        {classDef.tag}
      </div>
    </div>
  );
}
